/**
 * @file planner-configurator.js
 * @description Step-by-step configurator for the digital planner product. State, URL sync, LocalStorage, Add-to-Cart.
 * @section product-digital-planner
 *
 * ----------------------------------------------------------------------------
 * Phase 1 – Architektur-Entscheidung
 * ----------------------------------------------------------------------------
 * - jQuery im Theme: Ja, 3.6.0 (layout/theme.liquid), genutzt in popup.js, compare, product-card.js.
 * - Component-Pattern: Mix aus Vanilla (global.js, product-form als Custom Element) und jQuery; keine Web Components.
 * - State: 7 Kategorien, Bild-Key aus globalen Selections, URL-Sync, LocalStorage – mittel komplex, ein Modul beherrschbar.
 * - Kein Build-Step für Theme-Assets.
 *
 * Empfehlung: Vanilla JS (ES Modules). Begründung: Base Rules verlangen „Neue Features in Vanilla JS“; Konfigurator
 * ist eigenständig und muss nicht in bestehende jQuery-Ketten; State zentral mit einem Objekt abbildbar; testbar ohne DOM.
 * ----------------------------------------------------------------------------
 */

const STATE_VERSION = 1;
const STORAGE_TTL_DAYS = 7;
const STORAGE_KEY_PREFIX = 'planner_config_';

/**
 * Build map: categoryId -> Set of valid option IDs (for URL/localStorage validation).
 * @param {Array<{ id: string, options: Array<{ id: string }> }>} categories
 * @returns {Record<string, Set<string>>}
 */
export function getValidOptionsMap(categories) {
  const map = {};
  if (!Array.isArray(categories)) return map;
  for (const cat of categories) {
    if (cat && cat.id && Array.isArray(cat.options)) {
      map[cat.id] = new Set(cat.options.map((o) => o && o.id).filter(Boolean));
    }
  }
  return map;
}

/**
 * Apply a single selection. Pure function: returns new selections and completion flag.
 * @param {Record<string, string>} selections
 * @param {string} categoryId
 * @param {string} optionId
 * @param {string[]} requiredCategoryIds
 * @returns {{ selections: Record<string, string>, isComplete: boolean }}
 */
export function applySelection(selections, categoryId, optionId, requiredCategoryIds) {
  const next = { ...selections };
  if (categoryId && optionId) next[categoryId] = optionId;
  const ids = Array.isArray(requiredCategoryIds) ? requiredCategoryIds : [];
  const isComplete = ids.length > 0 && ids.every((id) => next[id] != null && next[id] !== '');
  return { selections: next, isComplete };
}

/**
 * Resolve product image URL. Builds a key from global categories in order, stopping at
 * the first category without a selection (partial global key). When all globals are selected
 * and `lastContributingCategoryId` is set, a specific suffix is attempted first, with
 * optional compound prefix via `category.image_key_prefix_from`. Falls back to the
 * (possibly partial) global key.
 * @param {Record<string, string>} selections
 * @param {Record<string, string>} imageMap
 * @param {Array<{ id: string, type: string, contributes_to_image?: boolean, image_key_prefix_from?: string, options: Array<{ id: string, image_key?: string }> }>} categories
 * @param {string|null} [lastContributingCategoryId]
 * @returns {string|null}
 */
export function resolveProductImage(selections, imageMap, categories, lastContributingCategoryId = null) {
  if (!selections || typeof imageMap !== 'object' || !Array.isArray(categories)) return null;

  const globalParts = [];
  let allGlobalsSelected = true;
  for (const cat of categories) {
    if (cat.type !== 'global') continue;
    const optionId = selections[cat.id];
    if (optionId == null || optionId === '') { allGlobalsSelected = false; break; }
    const option = cat.options && cat.options.find((o) => o && o.id === optionId);
    globalParts.push(option && option.image_key ? option.image_key : optionId);
  }
  if (globalParts.length === 0) return null;

  const globalKey = globalParts.join('__');

  if (allGlobalsSelected && lastContributingCategoryId) {
    const contribCat = categories.find(
      (c) => c.id === lastContributingCategoryId && c.contributes_to_image === true
    );
    if (contribCat) {
      const optionId = selections[contribCat.id];
      if (optionId != null && optionId !== '') {
        const option = contribCat.options && contribCat.options.find((o) => o && o.id === optionId);
        const thisImageKey = option && option.image_key ? option.image_key : optionId;

        if (contribCat.image_key_prefix_from) {
          const prefixCat = categories.find((c) => c.id === contribCat.image_key_prefix_from);
          const prefixOptId = prefixCat && selections[prefixCat.id];
          if (prefixOptId != null && prefixOptId !== '') {
            const prefixOpt = prefixCat.options && prefixCat.options.find((o) => o && o.id === prefixOptId);
            const prefixKey = prefixOpt && prefixOpt.image_key ? prefixOpt.image_key : prefixOptId;
            const compoundKey = globalKey + '__' + prefixKey + '__' + thisImageKey;
            if (imageMap[compoundKey] != null) return imageMap[compoundKey];
          }
        }

        const specificKey = globalKey + '__' + thisImageKey;
        if (imageMap[specificKey] != null) return imageMap[specificKey];
      }
    }
  }

  return imageMap[globalKey] ?? null;
}

/**
 * Resolve a conditional option image URL based on current selections.
 * @param {{ conditional_option_image?: Record<string, Record<string, string>> }} option
 * @param {Record<string, string>} selections
 * @returns {string|null}
 */
export function resolveOptionImage(option, selections) {
  if (!option?.conditional_option_image) return null;
  for (const [sourceCatId, mapping] of Object.entries(option.conditional_option_image)) {
    const val = selections?.[sourceCatId];
    if (val && mapping[val]) return mapping[val];
  }
  return null;
}

/**
 * Encode selections + optional currentStep to query string.
 * @param {Record<string, string>} selections
 * @param {number|null} [currentStep]
 * @returns {string}
 */
export function encodeConfigToURL(selections, currentStep = null) {
  if (!selections || typeof selections !== 'object') return '';
  const parts = Object.entries(selections)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
  if (typeof currentStep === 'number' && currentStep >= 0) {
    parts.unshift(`step=${currentStep}`);
  }
  return parts.join('&');
}

/**
 * Decode query string to selections + step index.
 * @param {string} queryString
 * @param {Record<string, Set<string>>} validOptions
 * @returns {{ selections: Record<string, string>, step: number|null }}
 */
export function decodeConfigFromURL(queryString, validOptions) {
  const out = { selections: {}, step: null };
  if (typeof queryString !== 'string' || !queryString.trim()) return out;
  if (typeof validOptions !== 'object' || validOptions === null) return out;
  const params = new URLSearchParams(queryString.startsWith('?') ? queryString.slice(1) : queryString);
  for (const [key, value] of params) {
    if (key === 'step') {
      const n = parseInt(value, 10);
      if (!Number.isNaN(n) && n >= 0) out.step = n;
      continue;
    }
    const set = validOptions[key];
    if (set && set.has(value)) out.selections[key] = value;
  }
  return out;
}

/**
 * Persist state to localStorage. Key: planner_config_{productId}. TTL 7 days.
 * @param {string} productId
 * @param {{ selections: Record<string, string>, completed: boolean, version: number, currentStep?: number }} state
 */
export function persistState(productId, state) {
  if (!productId) return;
  const key = STORAGE_KEY_PREFIX + productId;
  const payload = {
    ...state,
    version: state.version ?? STATE_VERSION,
    savedAt: Date.now()
  };
  try {
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (_) {}
}

/**
 * Restore state from localStorage. Returns null if missing, version mismatch, or older than 7 days.
 * @param {string} productId
 * @param {number} currentVersion
 * @returns {{ selections: Record<string, string>, completed: boolean, lastContributingCategoryId: string|null, currentStep: number } | null}
 */
export function restoreState(productId, currentVersion) {
  if (!productId) return null;
  const key = STORAGE_KEY_PREFIX + productId;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.version !== currentVersion) return null;
    const savedAt = data.savedAt;
    if (typeof savedAt === 'number' && Date.now() - savedAt > STORAGE_TTL_DAYS * 24 * 60 * 60 * 1000) return null;
    return {
      selections: data.selections && typeof data.selections === 'object' ? { ...data.selections } : {},
      completed: Boolean(data.completed),
      lastContributingCategoryId: typeof data.lastContributingCategoryId === 'string'
        ? data.lastContributingCategoryId : null,
      currentStep: typeof data.currentStep === 'number' && data.currentStep >= 0
        ? data.currentStep : 0,
      bundleSelection: data.bundleSelection === 'mini' || data.bundleSelection === 'maxi'
        ? data.bundleSelection : 'none'
    };
  } catch (_) {
    return null;
  }
}

/**
 * Remove stored state for product.
 * @param {string} productId
 */
export function clearState(productId) {
  if (!productId) return;
  try {
    localStorage.removeItem(STORAGE_KEY_PREFIX + productId);
  } catch (_) {}
}

/**
 * Build cart/add.js payload with line item properties. Returns null if not all required categories are set.
 * @param {string|number} variantId
 * @param {Record<string, string>} selections
 * @param {string[]} requiredCategoryIds
 * @param {Array<{ id: string, label?: string, options: Array<{ id: string, label?: string }> }>} [categories]
 * @returns {{ id: number, quantity: number, properties: Record<string, string> } | null}
 */
export function buildCartPayload(variantId, selections, requiredCategoryIds, categories) {
  const ids = Array.isArray(requiredCategoryIds) ? requiredCategoryIds : [];
  if (ids.length === 0) return null;
  const hasAll = ids.every((id) => selections && selections[id] != null && selections[id] !== '');
  if (!hasAll) return null;
  const variant = Number(variantId);
  if (Number.isNaN(variant) || variant < 1) return null;
  const properties = {};
  for (const id of ids) {
    const cat = (categories || []).find((c) => c.id === id);
    const key = cat?.label || id;
    const option = cat?.options?.find((o) => o.id === selections[id]);
    properties[key] = option?.label || String(selections[id]);
  }
  return { id: variant, quantity: 1, properties };
}

/**
 * Add configurator item to cart via fetch. Dispatches 'cart:updated' on success.
 * @param {{ id: number, quantity: number, properties: Record<string, string> }} payload
 * @param {string} cartAddUrl
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function addToCart(payload, cartAddUrl) {
  const url = cartAddUrl || '/cart/add.js';
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const message = data.description || data.message || (res.status === 422 ? 'Artikel nicht verfügbar.' : 'Fehler beim Hinzufügen.');
      return { ok: false, error: message };
    }
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, error: 'Netzwerkfehler. Bitte erneut versuchen.' };
  }
}

/**
 * Look up variant ID from SKU map.
 * @param {string} skuKey
 * @param {Record<string, number>} skuMap
 * @returns {number|null}
 */
export function lookupVariantId(skuKey, skuMap) {
  if (!skuKey || typeof skuMap !== 'object' || skuMap === null) return null;
  const id = skuMap[skuKey];
  return typeof id === 'number' && id > 0 ? id : null;
}

/**
 * Build SKU string from selections using sku_code per option.
 * Iterates ALL categories (including hidden/auto-selected ones).
 * @param {Record<string, string>} selections
 * @param {Array<{ id: string, options: Array<{ id: string, sku_code?: string }> }>} categories
 * @param {string} skuPrefix
 * @returns {string|null}
 */
export function buildSkuString(selections, categories, skuPrefix) {
  if (!selections || !Array.isArray(categories)) return null;
  const codes = [];
  for (const cat of categories) {
    const optionId = selections[cat.id];
    if (optionId == null || optionId === '') return null;
    const option = cat.options && cat.options.find((o) => o && o.id === optionId);
    if (!option || !option.sku_code) return null;
    codes.push(option.sku_code);
  }
  return skuPrefix + '-' + codes.join('-');
}

/**
 * Returns Set of category IDs that should be hidden (auto_select_single + exactly 1 allowed option).
 * @param {Array} categories
 * @param {Record<string, string>} selections
 * @returns {Set<string>}
 */
export function resolveHiddenCategories(categories, selections) {
  const hidden = new Set();
  if (!Array.isArray(categories) || !selections) return hidden;
  for (const cat of categories) {
    if (cat.auto_select_single !== true) continue;
    const allowed = resolveAllowedOptions(cat.id, categories, selections);
    if (allowed instanceof Set && allowed.size === 1) hidden.add(cat.id);
  }
  return hidden;
}

/**
 * Returns the first allowed optionId for the category at `categoryIndex`,
 * or null for index 0 (step 1 shows product.image, no preselection).
 */
export function getDefaultPreselection(categoryIndex, categories, selections) {
  if (categoryIndex === 0) return null;
  if (!Array.isArray(categories) || categoryIndex >= categories.length) return null;
  const cat = categories[categoryIndex];
  if (!cat || !Array.isArray(cat.options) || cat.options.length === 0) return null;
  const allowed = resolveAllowedOptions(cat.id, categories, selections);
  if (allowed !== null && allowed.size === 0) return null;
  const first = cat.options.find(opt => opt && opt.id && (allowed === null || allowed.has(opt.id)));
  return first ? first.id : null;
}

/**
 * Returns new selections with hidden categories auto-selected to their single allowed option.
 * When auto_select_single is true and allowed options expand to > 1, clears the selection
 * so the user must choose explicitly (symmetric with auto-set).
 * Idempotent. No mutation.
 * @param {Record<string, string>} selections
 * @param {Array} categories
 * @returns {Record<string, string>}
 */
export function applyAutoSelections(selections, categories) {
  if (!Array.isArray(categories) || !selections) return { ...selections };
  const next = { ...selections };
  for (const cat of categories) {
    if (cat.auto_select_single !== true) continue;
    const allowed = resolveAllowedOptions(cat.id, categories, selections);
    if (!(allowed instanceof Set)) continue; // no deps or source not yet selected
    if (allowed.size === 1) {
      next[cat.id] = [...allowed][0]; // auto-set (hidden step)
    } else if (allowed.size > 1) {
      delete next[cat.id]; // clear — user must choose explicitly
    }
    // size === 0: applyDependencyClearings already handles invalid selections
  }
  return next;
}

/**
 * Build cart payload using SKU map lookup. Returns {id, quantity:1, properties} or {error}.
 * @param {Record<string, string>} selections
 * @param {Array<{ id: string, label?: string, options: Array<{ id: string, sku_code?: string, label?: string }> }>} categories
 * @param {string} skuPrefix
 * @param {Record<string, number>} skuMap
 * @returns {{ id: number, quantity: number, properties: Record<string, string> }|{ error: string }}
 */
export function buildSkuCartPayload(selections, categories, skuPrefix, skuMap) {
  const sku = buildSkuString(selections, categories, skuPrefix);
  if (!sku) return { error: 'Bitte alle Optionen auswählen.' };
  const variantId = lookupVariantId(sku, skuMap);
  if (!variantId) return { error: 'Diese Kombination ist nicht verfügbar.' };
  const properties = {};
  for (const cat of (categories || [])) {
    const optionId = selections[cat.id];
    if (!optionId) continue;
    const option = cat.options?.find((o) => o.id === optionId);
    properties[cat.label || cat.id] = option?.label || String(optionId);
  }
  return { id: variantId, quantity: 1, properties };
}

/**
 * Resolve allowed option IDs for a category based on its dependencies and current selections.
 * Returns null if no filtering needed (no deps, or source not yet selected → show all).
 * Multiple deps → intersection.
 * @param {string} categoryId
 * @param {Array<{ id: string, dependencies?: Record<string, Record<string, string[]>> }>} categories
 * @param {Record<string, string>} selections
 * @returns {Set<string>|null}
 */
export function resolveAllowedOptions(categoryId, categories, selections) {
  if (!Array.isArray(categories) || !selections) return null;
  const cat = categories.find((c) => c.id === categoryId);
  if (!cat || !cat.dependencies || typeof cat.dependencies !== 'object') return null;

  const depEntries = Object.entries(cat.dependencies);
  if (depEntries.length === 0) return null;

  let result = null;
  for (const [sourceCatId, mapping] of depEntries) {
    const sourceVal = selections[sourceCatId];
    if (sourceVal == null || sourceVal === '') return null; // source not selected → no filtering
    const allowed = mapping[sourceVal];
    if (!Array.isArray(allowed)) return new Set(); // source selected but no mapping → nothing allowed
    const allowedSet = new Set(allowed);
    result = result ? new Set([...result].filter((id) => allowedSet.has(id))) : allowedSet;
  }
  return result;
}

/**
 * Clear selections that are no longer valid due to dependencies. Loops until stable.
 * @param {Record<string, string>} selections
 * @param {Array<{ id: string, dependencies?: Record<string, Record<string, string[]>> }>} categories
 * @returns {{ selections: Record<string, string>, cleared: string[] }}
 */
export function applyDependencyClearings(selections, categories) {
  if (!Array.isArray(categories)) return { selections: { ...selections }, cleared: [] };
  let current = { ...selections };
  const cleared = [];
  const maxIter = categories.length;
  for (let i = 0; i < maxIter; i++) {
    let changed = false;
    for (const cat of categories) {
      if (!current[cat.id]) continue;
      const allowed = resolveAllowedOptions(cat.id, categories, current);
      if (allowed !== null && !allowed.has(current[cat.id])) {
        delete current[cat.id];
        if (!cleared.includes(cat.id)) cleared.push(cat.id);
        changed = true;
      }
    }
    if (!changed) break;
  }
  return { selections: current, cleared };
}

/**
 * Gibt alle Kategorie-IDs zurück, die als Dependency-Source referenziert werden.
 * @param {Array<{ id: string, dependencies?: Record<string, any> }>} categories
 * @returns {string[]}
 */
export function getDependencySourceIds(categories) {
  if (!Array.isArray(categories)) return [];
  const sources = new Set();
  for (const cat of categories) {
    if (cat && cat.dependencies && typeof cat.dependencies === 'object') {
      for (const sourceId of Object.keys(cat.dependencies)) {
        sources.add(sourceId);
      }
    }
  }
  return [...sources];
}

/**
 * Gibt IDs aller Pflicht-Kategorien zurück: explizit required (default true)
 * PLUS alle Dependency-Sources.
 * @param {Array<{ id: string, required?: boolean, dependencies?: Record<string, any> }>} categories
 * @returns {string[]}
 */
export function getRequiredCategoryIds(categories) {
  if (!Array.isArray(categories)) return [];
  const depSources = new Set(getDependencySourceIds(categories));
  return categories
    .filter(cat => cat && cat.id && (cat.required !== false || depSources.has(cat.id)))
    .map(cat => cat.id);
}

/**
 * Leitet Tab-States für alle Steps ab.
 * - 'completed': selections[id] gesetzt (!falsy) — unabhängig von Position
 * - 'skipped':   index < currentStep UND selections[id] fehlt
 *                ODER index > currentStep UND id ist in invalidatedIds
 * - 'active':    index === currentStep
 * - 'pending':   index > currentStep UND selections[id] fehlt UND nicht invalidiert
 * @param {Record<string, string>} selections
 * @param {number} currentStep
 * @param {Array<{ id: string }>} categories
 * @param {Set<string>|null} [invalidatedIds] IDs dependency-geleeerter Steps → 'skipped' statt 'pending'
 * @returns {Array<'active'|'completed'|'pending'|'skipped'>}
 */
export function deriveTabStates(selections, currentStep, categories, invalidatedIds = null) {
  if (!Array.isArray(categories)) return [];
  return categories.map((cat, i) => {
    if (i === currentStep) return 'active';
    if (selections && selections[cat.id]) return 'completed';
    if (i < currentStep) return 'skipped';
    if (invalidatedIds instanceof Set && invalidatedIds.has(cat.id)) return 'skipped';
    return 'pending';
  });
}

/**
 * Ob der Weiter-Button für den aktuellen Step aktiv sein soll.
 * @param {number} currentStep
 * @param {Record<string, string>} selections
 * @param {Array<{ id: string, required?: boolean }>} categories
 * @returns {boolean}
 */
export function canAdvance(currentStep, selections, categories) {
  if (!Array.isArray(categories) || currentStep < 0 || currentStep >= categories.length) return false;
  const cat = categories[currentStep];
  if (cat.required !== false) return Boolean(selections && selections[cat.id]);
  return true;
}

/**
 * Ob der Cart-Button aktiv sein soll (alle required Steps ausgefüllt).
 * @param {Record<string, string>} selections
 * @param {Array<{ id: string, required?: boolean, dependencies?: Record<string, any> }>} categories
 * @returns {boolean}
 */
export function canAddToCart(selections, categories) {
  const requiredIds = getRequiredCategoryIds(categories);
  if (requiredIds.length === 0) return false;
  return requiredIds.every(id => selections && selections[id]);
}

/**
 * Index des ersten skipped Steps (keine Selektion, vor currentStep).
 * @param {Record<string, string>} selections
 * @param {number} currentStep
 * @param {Array<{ id: string }>} categories
 * @returns {number} Index oder -1
 */
export function firstSkippedStepIndex(selections, currentStep, categories) {
  if (!Array.isArray(categories)) return -1;
  for (let i = 0; i < currentStep && i < categories.length; i++) {
    if (!selections || !selections[categories[i].id]) return i;
  }
  return -1;
}

/**
 * Returns all price entries from skuPriceMap reachable given current (partial) selections.
 * For each category: uses selected option's sku_code, or all options' sku_codes if unselected.
 * Cross-product enumerates complete SKU strings and returns matching price entries.
 * Returns [] if skuPriceMap is absent, any category lacks sku_codes, or no SKUs match.
 * @param {Record<string, string>} selections
 * @param {Array<{ id: string, options: Array<{ id: string, sku_code?: string }> }>} categories
 * @param {string} skuPrefix
 * @param {Record<string, { cents: number, formatted: string }>|null} skuPriceMap
 * @returns {Array<{ cents: number, formatted: string }>}
 */
export function getReachablePrices(selections, categories, skuPrefix, skuPriceMap) {
  if (!skuPriceMap || typeof skuPriceMap !== 'object') return [];
  if (!Array.isArray(categories) || categories.length === 0) return [];
  if (!selections) return [];

  const perCat = [];
  for (const cat of categories) {
    const selectedId = selections[cat.id];
    if (selectedId != null && selectedId !== '') {
      const opt = Array.isArray(cat.options) && cat.options.find(o => o && o.id === selectedId);
      if (!opt || !opt.sku_code) return [];
      perCat.push([opt.sku_code]);
    } else {
      if (!Array.isArray(cat.options)) return [];
      const codes = cat.options.filter(o => o && o.sku_code).map(o => o.sku_code);
      if (codes.length === 0) return [];
      perCat.push(codes);
    }
  }

  const prices = [];
  const codes = [];
  function enumerate(idx) {
    if (idx === perCat.length) {
      const sku = skuPrefix + '-' + codes.join('-');
      const entry = skuPriceMap[sku];
      if (entry) prices.push(entry);
      return;
    }
    for (const code of perCat[idx]) {
      codes.push(code);
      enumerate(idx + 1);
      codes.pop();
    }
  }
  enumerate(0);
  return prices;
}

/**
 * Derives display price from current selections.
 * Returns null if no price data available or no SKUs match.
 * @param {Record<string, string>} selections
 * @param {Array} categories
 * @param {string} skuPrefix
 * @param {Record<string, { cents: number, formatted: string }>|null} skuPriceMap
 * @returns {{ formatted: string, isFromPrice: boolean }|null}
 */
export function derivePrice(selections, categories, skuPrefix, skuPriceMap) {
  const prices = getReachablePrices(selections, categories, skuPrefix, skuPriceMap);
  if (prices.length === 0) return null;

  const seen = new Map();
  for (const p of prices) {
    if (!seen.has(p.cents)) seen.set(p.cents, p);
  }
  const unique = [...seen.values()];

  if (unique.length === 1) return { formatted: unique[0].formatted, isFromPrice: false, cents: unique[0].cents };

  const lowest = unique.reduce((min, p) => p.cents < min.cents ? p : min, unique[0]);
  return { formatted: lowest.formatted, isFromPrice: true, cents: lowest.cents };
}

/**
 * Parse a JSON response text from the product.variants-json Liquid template.
 * Returns null on any parse or validation error.
 * @param {string} text - Raw response text (may have leading/trailing whitespace from Liquid)
 * @returns {{ pages: number, skuMap: Record<string, number>, priceMap: Record<string, { cents: number, formatted: string }> } | null}
 */
export function parseVariantMapsResponse(text) {
  try {
    const data = JSON.parse((text || '').trim());
    if (!data || typeof data !== 'object') return null;
    if (typeof data.pages !== 'number' || data.pages < 1) return null;
    if (!data.skuMap || Array.isArray(data.skuMap) || typeof data.skuMap !== 'object') return null;
    return {
      pages: data.pages,
      skuMap: data.skuMap,
      priceMap: (data.priceMap && !Array.isArray(data.priceMap) && typeof data.priceMap === 'object') ? data.priceMap : {}
    };
  } catch (_) {
    return null;
  }
}

/**
 * Merge variant map pages into a single combined map.
 * Later pages overwrite duplicate keys from earlier pages.
 * @param {Array<{ skuMap: Record<string, number>, priceMap: Record<string, object> }>} pages
 * @returns {{ skuMap: Record<string, number>, priceMap: Record<string, object> }}
 */
export function mergeVariantMaps(pages) {
  const skuMap = {};
  const priceMap = {};
  for (const page of (pages || [])) {
    Object.assign(skuMap, page.skuMap || {});
    Object.assign(priceMap, page.priceMap || {});
  }
  return { skuMap, priceMap };
}

export function parseBundleConfig(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const result = {};
  if (typeof raw.color_category_id === 'string') {
    result.color_category_id = raw.color_category_id;
  }
  for (const key of ['mini', 'maxi']) {
    const opt = raw[key];
    if (opt && typeof opt === 'object' &&
        typeof opt.product_handle === 'string' &&
        typeof opt.discount_rate === 'number') {
      result[key] = opt;
    }
  }
  return (result.mini || result.maxi) ? result : null;
}

export function formatBundlePrice(priceCents, discountRate) {
  if (typeof priceCents !== 'number' || priceCents < 0) return { original: '', discounted: '' };
  if (typeof discountRate !== 'number' || discountRate < 0 || discountRate >= 1) return { original: '', discounted: '' };
  const fmt = (cents) => {
    const whole = Math.floor(cents / 100);
    const frac = String(cents % 100).padStart(2, '0');
    return `${whole},${frac} €`;
  };
  return {
    original: fmt(priceCents),
    discounted: fmt(Math.round(priceCents * (1 - discountRate)))
  };
}

export function buildBundleCartItem(variantId) {
  return { id: variantId, quantity: 1 };
}

export function lookupBundleVariant(variantMap, bundleKey, colorLabel) {
  if (!variantMap || typeof variantMap !== 'object') return null;
  const keyMap = variantMap[bundleKey];
  if (!keyMap || typeof keyMap !== 'object') return null;
  const variant = keyMap[colorLabel];
  if (!variant || typeof variant.id !== 'number') return null;
  return { id: variant.id, price: variant.price, image: variant.image ?? null };
}

export function encodeBundleSelection(selection) {
  return selection === 'mini' || selection === 'maxi' ? selection : '';
}

export function decodeBundleSelection(searchString) {
  const qs = searchString || '';
  const params = new URLSearchParams(qs.startsWith('?') ? qs.slice(1) : qs);
  const val = params.get('bundle');
  return val === 'mini' || val === 'maxi' ? val : 'none';
}

// ── DOM-gebundenes Modul (nur im Browser); von Coverage ausgenommen ───────────
/* c8 ignore start */
const PlannerConfigurator = (() => {
  let skuPriceMap = null;

  let state = {
    productId: '',
    variantId: '',
    selections: {},
    currentStep: 0,
    completed: false,
    version: STATE_VERSION,
    lastContributingCategoryId: null,
    dependencyInvalidatedIds: new Set(),
    bundleSelection: 'none'
  };
  let categories = [];
  let imageMap = {};
  let validOptions = {};
  let skuMap = null;
  let skuMapLoading = false;
  let skuPrefix = '';
  let minAnimDuration = 1500;
  let elements = {};
  let abortController = null;
  let _isTransitioning = false;
  let bundleConfig = null;
  let bundleVariantMap = null;

  function _getOrderedIds() {
    return categories.map(c => c.id).filter(Boolean);
  }

  function _getBundleColorLabel() {
    if (!bundleConfig?.color_category_id) return null;
    const catId = bundleConfig.color_category_id;
    const cat = categories.find(c => c.id === catId);
    if (!cat) return null;
    const optId = state.selections[catId];
    if (!optId) return null;
    return cat.options.find(o => o.id === optId)?.label ?? null;
  }

  function loadFromDataScript() {
    const el = document.getElementById('planner-configurator-data');
    if (!el || el.tagName !== 'SCRIPT' || el.type !== 'application/json') return;
    try {
      const data = JSON.parse(el.textContent);
      categories = Array.isArray(data.categories) ? data.categories : [];
      imageMap = (data.image_map && typeof data.image_map === 'object') ? data.image_map : {};
      skuPrefix = typeof data.sku_prefix === 'string' ? data.sku_prefix : '';
      validOptions = getValidOptionsMap(categories);
    } catch (_) {}

    const skuEl = document.getElementById('planner-sku-map');
    if (skuEl && skuEl.tagName === 'SCRIPT' && skuEl.type === 'application/json') {
      try {
        const parsed = JSON.parse(skuEl.textContent);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          skuMap = parsed;
        }
      } catch (_) {}
    }

    const priceEl = document.getElementById('planner-sku-price-map');
    if (priceEl && priceEl.tagName === 'SCRIPT' && priceEl.type === 'application/json') {
      try {
        const parsed = JSON.parse(priceEl.textContent);
        if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          skuPriceMap = parsed;
        }
      } catch (_) {}
    }

    const bundleEl = document.getElementById('planner-bundle-data');
    if (bundleEl && bundleEl.tagName === 'SCRIPT' && bundleEl.type === 'application/json') {
      try {
        bundleConfig = parseBundleConfig(JSON.parse(bundleEl.textContent));
      } catch (_) {}
    }

    const bundleVariantsEl = document.getElementById('planner-bundle-variants');
    if (bundleVariantsEl && bundleVariantsEl.tagName === 'SCRIPT' && bundleVariantsEl.type === 'application/json') {
      try {
        bundleVariantMap = JSON.parse(bundleVariantsEl.textContent);
      } catch (_) {}
    }
  }

  async function _fetchAllVariantMaps(handle) {
    skuMapLoading = true;
    const fetched = [];
    try {
      const baseUrl = '/products/' + encodeURIComponent(handle) + '?view=variants-json';
      const res1 = await fetch(baseUrl);
      if (!res1.ok) return;
      const parsed1 = parseVariantMapsResponse(await res1.text());
      if (!parsed1) return;
      // If only 1 page, the inline map already covers all variants — nothing to do
      if (parsed1.pages <= 1) return;
      fetched.push({ skuMap: parsed1.skuMap, priceMap: parsed1.priceMap });
      for (let p = 2; p <= parsed1.pages; p++) {
        const res = await fetch(baseUrl + '&page=' + p);
        if (!res.ok) break;
        const parsed = parseVariantMapsResponse(await res.text());
        if (!parsed) break;
        fetched.push({ skuMap: parsed.skuMap, priceMap: parsed.priceMap });
      }
      if (fetched.length === parsed1.pages) {
        const merged = mergeVariantMaps(fetched);
        if (Object.keys(merged.skuMap).length > 0) {
          skuMap = merged.skuMap;
          skuPriceMap = Object.keys(merged.priceMap).length > 0 ? merged.priceMap : null;
        }
      }
    } catch (_) {
      // Silently fall back to inline map (may be partial for >250-variant products)
    } finally {
      skuMapLoading = false;
      _updatePrice();
      _render();
    }
  }

  function init() {
    const container = document.querySelector('[data-planner]');
    if (!container) return;

    loadFromDataScript();
    state.productId = container.dataset.productId || '';
    state.variantId = container.dataset.variantId || '';

    const variantCount = parseInt(container.dataset.variantCount || '0', 10);
    const productHandle = container.dataset.productHandle || '';
    if (variantCount >= 250 && productHandle) {
      _fetchAllVariantMaps(productHandle); // fire-and-forget; pages<=1 early-returns if inline map is complete
    }

    // Read animation duration from data attribute (set by section liquid)
    minAnimDuration = parseInt(container.dataset.animDuration || '1500', 10) || 1500;

    // Restore state from URL or localStorage
    const { selections: fromUrlSels, step: fromUrlStep } = decodeConfigFromURL(window.location.search, validOptions);
    const fromStorage = restoreState(state.productId, STATE_VERSION);

    if (Object.keys(fromUrlSels).length > 0) {
      state.selections = fromUrlSels;
      if (fromUrlStep !== null) state.currentStep = fromUrlStep;
    } else if (fromStorage && Object.keys(fromStorage.selections).length > 0) {
      state.selections = fromStorage.selections;
      state.currentStep = fromStorage.currentStep || 0;
      if (fromStorage.lastContributingCategoryId) {
        state.lastContributingCategoryId = fromStorage.lastContributingCategoryId;
      }
    }
    if (bundleConfig) {
      const urlBundle = decodeBundleSelection(window.location.search);
      if (urlBundle !== 'none') {
        state.bundleSelection = urlBundle;
      } else if (fromStorage && fromStorage.bundleSelection !== 'none') {
        state.bundleSelection = fromStorage.bundleSelection;
      }
    }

    // Apply dependency clearings
    const depResult = applyDependencyClearings(state.selections, categories);
    state.selections = depResult.selections;
    for (const id of depResult.cleared) state.dependencyInvalidatedIds.add(id);
    if (depResult.cleared.includes(state.lastContributingCategoryId)) {
      state.lastContributingCategoryId = null;
    }
    state.selections = applyAutoSelections(state.selections, categories);

    // Clamp currentStep to visible range
    const _initVisible = categories.filter(c => !resolveHiddenCategories(categories, state.selections).has(c.id));
    const _initMaxStep = _initVisible.length - 1 + (bundleConfig ? 1 : 0);
    state.currentStep = Math.max(0, Math.min(state.currentStep, _initMaxStep));
    state.completed = canAddToCart(state.selections, categories);

    // Cache DOM elements
    elements.container = container;
    elements.steps = Array.from(container.querySelectorAll('[data-planner-step]'));
    elements.options = container.querySelectorAll('[data-planner-option]');
    elements.tabs = Array.from(container.querySelectorAll('[data-planner-tab]'));
    elements.dots = Array.from(container.querySelectorAll('[data-planner-dot]'));
    elements.dotsCount = container.querySelector('[data-planner-dots-count]');
    elements.dotsName = container.querySelector('[data-planner-dots-name]');
    elements.image = container.querySelector('[data-planner-image]');
    elements.summary = container.querySelector('[data-planner-summary]');
    elements.cartBtn = container.querySelector('[data-planner-cart-btn]');
    elements.cartArea = container.querySelector('[data-planner-cart-area]');
    elements.errorEl = container.querySelector('[data-planner-error]');
    elements.animOverlay = container.querySelector('[data-planner-animation]');
    elements.mobileBack = container.querySelector('[data-planner-mobile-back]');
    elements.mobileNext = container.querySelector('[data-planner-mobile-next]');
    elements.stepsCol = container.querySelector('.planner__steps-col');
    elements.overviewBtn = container.querySelector('[data-planner-overview-btn]');
    elements.overviewDialog = container.querySelector('[data-planner-overview-dialog]');
    elements.overviewBody = container.querySelector('[data-planner-overview-body]');
    elements.priceEl = container.querySelector('[data-planner-price]');
    elements.bundleStep = container.querySelector('[data-planner-bundle-step]') || null;
    elements.bundleTab = container.querySelector('[data-planner-bundle-tab]') || null;
    elements.bundleDot = container.querySelector('[data-planner-bundle-dot]') || null;

    abortController = new AbortController();
    _observeHeaderVisibility();
    _bindEvents();

    // Show correct initial step (hide all others)
    elements.steps.forEach((step, i) => {
      step.hidden = i !== state.currentStep;
    });
    if (elements.bundleStep) {
      elements.bundleStep.hidden = !bundleConfig || state.currentStep !== _initVisible.length;
    }

    _render();
    _updateImage();
    _updateSummary();
    _updatePrice();
    _preloadDefaultImages();
  }

  function _observeHeaderVisibility() {
    const header = document.querySelector('.shopify-section-header');
    if (!header || !elements.container) return;
    const update = () => {
      if (header.classList.contains('shopify-section-header-hidden')) {
        elements.container.style.setProperty('--_header-h', '0px');
      } else {
        elements.container.style.removeProperty('--_header-h');
      }
    };
    update();
    const obs = new MutationObserver(update);
    obs.observe(header, { attributes: true, attributeFilter: ['class'] });
    abortController.signal.addEventListener('abort', () => obs.disconnect());
  }

  function _bindEvents() {
    const { signal } = abortController;

    // Option selection (event delegation)
    elements.container.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-planner-option]');
      if (!opt) return;
      const categoryId = opt.getAttribute('data-category-id');
      const optionId = opt.getAttribute('data-option-id');
      if (!categoryId || !optionId) return;
      _selectOption(categoryId, optionId);
    }, { signal });

    // Bundle option selection
    elements.container.addEventListener('click', (e) => {
      const opt = e.target.closest('[data-bundle-option]');
      if (!opt) return;
      const selection = opt.getAttribute('data-bundle-option');
      if (selection) _selectBundle(selection);
    }, { signal });

    // Desktop tab-strip clicks
    elements.tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const idx = parseInt(tab.dataset.stepIndex, 10);
        if (tab.dataset.state !== 'pending') _goToStep(idx);
      }, { signal });
    });

    // Bundle tab click
    if (elements.bundleTab) {
      elements.bundleTab.addEventListener('click', () => {
        const hiddenIds = resolveHiddenCategories(categories, state.selections);
        const visLen = categories.filter(c => !hiddenIds.has(c.id)).length;
        if (elements.bundleTab.dataset.state !== 'pending') _goToStep(visLen);
      }, { signal });
    }

    // Step-level Weiter/Zurück buttons
    elements.container.addEventListener('click', (e) => {
      if (e.target.closest('[data-planner-next]')) _onNext();
      if (e.target.closest('[data-planner-back]')) _onBack();
    }, { signal });

    // Mobile nav buttons
    if (elements.mobileNext) {
      elements.mobileNext.addEventListener('click', _onNext, { signal });
    }
    if (elements.mobileBack) {
      elements.mobileBack.addEventListener('click', _onBack, { signal });
    }

    // Cart button
    if (elements.cartBtn) {
      elements.cartBtn.addEventListener('click', _onAddToCart, { signal });
    }

    if (elements.overviewBtn) {
      elements.overviewBtn.addEventListener('click', () => {
        _updateOverviewDialog();
        elements.overviewDialog?.showModal();
      }, { signal });
    }

    if (elements.overviewDialog) {
      // Close on backdrop click
      elements.overviewDialog.addEventListener('click', (e) => {
        if (e.target === elements.overviewDialog) elements.overviewDialog.close();
      }, { signal });
      // Close button
      const closeBtn = elements.overviewDialog.querySelector('[data-planner-overview-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', () => elements.overviewDialog.close(), { signal });
      }
    }
  }

  function _selectOption(categoryId, optionId) {
    const { selections } = applySelection(state.selections, categoryId, optionId, _getOrderedIds());
    const depResult = applyDependencyClearings(selections, categories);
    // Update dependency-invalidated tracking.
    // Reads depResult.selections (post-clearing snapshot), NOT state.selections (still old here).
    for (const id of depResult.cleared) state.dependencyInvalidatedIds.add(id);
    for (const id of [...state.dependencyInvalidatedIds]) {
      if (depResult.selections[id]) state.dependencyInvalidatedIds.delete(id);
    }
    state.selections = depResult.selections;
    state.selections = applyAutoSelections(state.selections, categories);
    // Re-apply explicit user selection if applyAutoSelections cleared a valid choice.
    // applyAutoSelections clears auto_select_single categories with >1 allowed options
    // (to reset stale auto-values when source changes), but can't distinguish an
    // auto-set value from a user-clicked one — so we re-assert the user's pick here.
    if (depResult.selections[categoryId] === optionId && state.selections[categoryId] !== optionId) {
      state.selections = { ...state.selections, [categoryId]: optionId };
    }
    state.completed = canAddToCart(state.selections, categories);

    const cat = categories.find(c => c.id === categoryId);
    if (cat && cat.contributes_to_image === true) {
      state.lastContributingCategoryId = categoryId;
    }
    if (depResult.cleared.includes(state.lastContributingCategoryId)) {
      state.lastContributingCategoryId = null;
    }

    _render();
    _updateImage();
    _updateSummary();
    _updatePrice();
    persistState(state.productId, state);
    _syncURL();
  }

  function _selectBundle(selection) {
    if (selection !== 'none' && selection !== 'mini' && selection !== 'maxi') return;
    if (selection !== 'none' && !bundleConfig?.[selection]) return;
    state.bundleSelection = selection;
    _render();
    _updateSummary();
    _updatePrice();
    persistState(state.productId, state);
    _syncURL();
  }

  async function _goToStep(index) {
    if (_isTransitioning) return;
    const _hiddenIds = resolveHiddenCategories(categories, state.selections);
    const _visLen = categories.filter(c => !_hiddenIds.has(c.id)).length;
    const _maxStep = _visLen - 1 + (bundleConfig ? 1 : 0);
    if (index < 0 || index > _maxStep) return;

    const _isBundleTarget = bundleConfig && index === _visLen;
    const _isBundleCurrent = bundleConfig && state.currentStep === _visLen;
    const oldStepEl = _isBundleCurrent ? elements.bundleStep : elements.steps[state.currentStep];
    const newStepEl = _isBundleTarget ? elements.bundleStep : elements.steps[index];
    if (!oldStepEl || !newStepEl || oldStepEl === newStepEl) return;

    _isTransitioning = true;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const direction = index > state.currentStep ? 1 : -1;
    const dur = prefersReduced ? 0 : 180;
    const enterDur = prefersReduced ? 0 : dur + 20;

    try {
      await oldStepEl.animate(
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: `translateY(${direction * -20}px)` }
        ],
        { duration: dur, easing: 'ease-in', fill: 'forwards' }
      ).finished;
      oldStepEl.hidden = true;
      oldStepEl.getAnimations().forEach(a => a.cancel());

      state.currentStep = index;

      newStepEl.hidden = false;
      await newStepEl.animate(
        [
          { opacity: 0, transform: `translateY(${direction * 20}px)` },
          { opacity: 1, transform: 'translateY(0)' }
        ],
        { duration: enterDur, easing: 'ease-out', fill: 'forwards' }
      ).finished;
      newStepEl.getAnimations().forEach(a => a.cancel());
    } finally {
      _isTransitioning = false;
    }

    if (!_isBundleTarget && categories[index]) {
      state.dependencyInvalidatedIds.delete(categories[index].id);

      const enteringCat = categories[index];
      if (index > 0) {
        const hiddenIds = resolveHiddenCategories(categories, state.selections);
        if (enteringCat && !state.selections[enteringCat.id] && !hiddenIds.has(enteringCat.id)) {
          const defaultOptId = getDefaultPreselection(index, categories, state.selections);
          if (defaultOptId) {
            const result = applySelection(state.selections, enteringCat.id, defaultOptId, _getOrderedIds());
            state.selections = result.selections;
            const depResult = applyDependencyClearings(state.selections, categories);
            for (const id of depResult.cleared) state.dependencyInvalidatedIds.add(id);
            state.selections = depResult.selections;
            if (enteringCat.contributes_to_image) state.lastContributingCategoryId = enteringCat.id;
          }
        }
      }

      if (enteringCat) {
        if (enteringCat.contributes_to_image === true && state.selections[enteringCat.id]) {
          state.lastContributingCategoryId = enteringCat.id;
          _updateImage();
        } else if (enteringCat.type === 'global') {
          state.lastContributingCategoryId = null;
          _updateImage(_buildGlobalScopedSelections(index), null);
        }
      }

      _preloadStepImages(index);
    }

    _render();
    _updatePrice();
    persistState(state.productId, state);
    _syncURL();

    const tab = _isBundleTarget ? elements.bundleTab : elements.tabs[index];
    if (tab) tab.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
  }

  function _onNext() {
    const hiddenIds = resolveHiddenCategories(categories, state.selections);
    const visibleCategories = categories.filter(c => !hiddenIds.has(c.id));

    if (bundleConfig && state.currentStep === visibleCategories.length) return;

    if (!canAdvance(state.currentStep, state.selections, categories)) return;
    const nextStep = state.currentStep + 1;

    if (nextStep >= visibleCategories.length) {
      if (bundleConfig) {
        _goToStep(nextStep);
        return;
      }
      elements.container.querySelector('[data-planner-cart-area]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    _goToStep(nextStep);
  }

  function _onBack() {
    if (state.currentStep <= 0) return;
    _goToStep(state.currentStep - 1);
  }

  function _render() {
    if (elements.errorEl) elements.errorEl.hidden = true;

    const hiddenIds = resolveHiddenCategories(categories, state.selections);
    const visibleCategories = categories.filter(c => !hiddenIds.has(c.id));

    // Cap currentStep to visible range (bundle step index is visibleCategories.length)
    const _maxStep = visibleCategories.length - 1 + (bundleConfig ? 1 : 0);
    if (state.currentStep > _maxStep) {
      state.currentStep = Math.max(0, _maxStep);
    }

    const tabStates = deriveTabStates(state.selections, state.currentStep, categories, state.dependencyInvalidatedIds);

    // Update tab-strip (hide tabs for hidden categories)
    elements.tabs.forEach((tab, i) => {
      if (categories[i] && hiddenIds.has(categories[i].id)) {
        tab.hidden = true;
        return;
      }
      tab.hidden = false;
      const s = tabStates[i] || 'pending';
      tab.dataset.state = s;
      tab.setAttribute('aria-selected', s === 'active' ? 'true' : 'false');
      const numEl = tab.querySelector('.wizard-tab-num');
      if (numEl) numEl.textContent = s === 'completed' ? '✓' : String(i + 1);
    });

    // Update mobile dots (hide dots for hidden categories)
    elements.dots.forEach((dot, i) => {
      if (categories[i] && hiddenIds.has(categories[i].id)) {
        dot.hidden = true;
        return;
      }
      dot.hidden = false;
      dot.dataset.state = tabStates[i] || 'pending';
    });

    // Ensure hidden category step panels stay hidden
    elements.steps.forEach((step, i) => {
      if (categories[i] && hiddenIds.has(categories[i].id)) step.hidden = true;
    });

    // Update step counters to reflect visible step position/total (#28)
    const visibleTotal = visibleCategories.length;
    elements.steps.forEach((step, i) => {
      if (!categories[i] || hiddenIds.has(categories[i].id)) return;
      const visibleIdx = visibleCategories.findIndex(c => c.id === categories[i].id);
      const counter = step.querySelector('[data-planner-step-counter]');
      if (counter && visibleIdx !== -1) {
        counter.textContent = `Schritt ${visibleIdx + 1} von ${visibleTotal}`;
      }
    });

    // Update mobile dots info text
    const _totalForDots = visibleCategories.length + (bundleConfig ? 1 : 0);
    const _isOnBundleStep = bundleConfig && state.currentStep === visibleCategories.length;
    if (elements.dotsCount) {
      elements.dotsCount.textContent = `Schritt ${state.currentStep + 1} von ${_totalForDots}`;
    }
    if (elements.dotsName) {
      elements.dotsName.textContent = _isOnBundleStep
        ? 'Bundle'
        : (categories[state.currentStep] ? categories[state.currentStep].label || '' : '');
    }

    // Update mobile back/next buttons
    if (elements.mobileBack) elements.mobileBack.disabled = state.currentStep === 0;
    if (elements.mobileNext) {
      const _totalSteps = visibleCategories.length + (bundleConfig ? 1 : 0);
      const isLast = state.currentStep === _totalSteps - 1;
      elements.mobileNext.disabled = isLast || !canAdvance(state.currentStep, state.selections, categories);
    }

    // Update Weiter buttons within steps
    elements.steps.forEach((step, i) => {
      const nextBtn = step.querySelector('[data-planner-next]');
      if (!nextBtn) return;
      const enabled = canAdvance(i, state.selections, categories);
      nextBtn.disabled = !enabled;
      nextBtn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      const _isLastVisCat = categories[i] === visibleCategories[visibleCategories.length - 1];
      nextBtn.hidden = _isLastVisCat && !bundleConfig;
    });

    // Update option aria-checked
    elements.options.forEach(opt => {
      const categoryId = opt.getAttribute('data-category-id');
      const optionId = opt.getAttribute('data-option-id');
      const isSelected = state.selections[categoryId] === optionId;
      opt.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      // Update checkmark text content for screen readers
      const check = opt.querySelector('.planner__option-check');
      if (check) check.setAttribute('aria-hidden', 'true');

      const allowed = resolveAllowedOptions(categoryId, categories, state.selections);
      opt.toggleAttribute('inert', allowed !== null && !allowed.has(optionId));

      const imgEl = opt.querySelector('[data-planner-option-img]');
      if (imgEl) {
        const cat = categories.find(c => c.id === categoryId);
        const catOption = cat && cat.options && cat.options.find(o => o && o.id === optionId);
        const imgUrl = resolveOptionImage(catOption, state.selections);
        imgEl.src = imgUrl || '';
      }
    });

    // Update selection labels in step headings
    elements.steps.forEach((step, i) => {
      const labelEl = step.querySelector('[data-planner-selection-label]');
      if (!labelEl) return;
      const cat = categories[i];
      if (!cat) return;
      const selectedId = state.selections[cat.id];
      const opt = selectedId && cat.options && cat.options.find(o => o && o.id === selectedId);
      labelEl.textContent = opt ? `\u2014 ${opt.label || selectedId}` : '';
    });

    // Cart area
    const cartReady = canAddToCart(state.selections, categories);
    if (elements.cartBtn) {
      elements.cartBtn.disabled = !cartReady;
      elements.cartBtn.setAttribute('aria-disabled', cartReady ? 'false' : 'true');
    }

    // Cart area and overview: visible only on last step (bundle step if configured)
    const _lastStepIdx = visibleCategories.length - 1 + (bundleConfig ? 1 : 0);
    if (elements.cartArea) elements.cartArea.hidden = state.currentStep !== _lastStepIdx;
    if (elements.overviewBtn) elements.overviewBtn.hidden = state.currentStep !== _lastStepIdx;

    // Bundle step: counter, tab state, dot state, option aria-checked
    if (bundleConfig) {
      const _bundleNum = visibleCategories.length + 1;
      const _isBundleActive = state.currentStep === visibleCategories.length;

      if (elements.bundleStep) {
        const counter = elements.bundleStep.querySelector('[data-planner-step-counter]');
        if (counter) counter.textContent = `Schritt ${_bundleNum} von ${_bundleNum}`;
        elements.bundleStep.querySelectorAll('[data-bundle-option]').forEach(opt => {
          const val = opt.getAttribute('data-bundle-option');
          opt.setAttribute('aria-checked', val === state.bundleSelection ? 'true' : 'false');
        });
      }

      if (elements.bundleTab) {
        const _isBundleCompleted = !_isBundleActive && state.bundleSelection !== 'none';
        const _bundleState = _isBundleActive ? 'active' : _isBundleCompleted ? 'completed' : 'pending';
        elements.bundleTab.dataset.state = _bundleState;
        elements.bundleTab.setAttribute('aria-selected', _isBundleActive ? 'true' : 'false');
        const numEl = elements.bundleTab.querySelector('.wizard-tab-num');
        if (numEl) numEl.textContent = _isBundleCompleted ? '✓' : String(_bundleNum);
      }

      if (elements.bundleDot) {
        const _isBundleDotCompleted = !_isBundleActive && state.bundleSelection !== 'none';
        elements.bundleDot.dataset.state = _isBundleActive ? 'active' : _isBundleDotCompleted ? 'completed' : 'pending';
      }

      if (elements.bundleStep && bundleVariantMap) {
        const colorLabel = _getBundleColorLabel();
        const plannerResult = derivePrice(state.selections, categories, skuPrefix, skuPriceMap);
        const plannerCents = (plannerResult && !plannerResult.isFromPrice) ? plannerResult.cents : 0;
        ['mini', 'maxi'].forEach(key => {
          const opt = bundleConfig[key];
          if (!opt) return;
          const variant = lookupBundleVariant(bundleVariantMap, key, colorLabel);
          if (!variant) return;
          const { original, discounted } = formatBundlePrice(plannerCents + variant.price, opt.discount_rate);
          const origEl = elements.bundleStep.querySelector(`[data-bundle-price-original="${key}"]`);
          const discEl = elements.bundleStep.querySelector(`[data-bundle-price-discounted="${key}"]`);
          if (origEl) origEl.textContent = original;
          if (discEl) discEl.textContent = discounted;
          const imgEl = elements.bundleStep.querySelector(`[data-bundle-img="${key}"]`);
          if (imgEl && variant.image) imgEl.src = variant.image;
          const badgeEl = elements.bundleStep.querySelector(`[data-bundle-discount-badge="${key}"]`);
          if (badgeEl) badgeEl.textContent = `−${Math.round(opt.discount_rate * 100)} %`;
        });
        const noneImgEl = elements.bundleStep.querySelector('[data-bundle-img="none"]');
        if (noneImgEl && elements.image?.src) noneImgEl.src = elements.image.src;
      }
    }
  }

  function _buildGlobalScopedSelections(upToStepIndex) {
    const scoped = {};
    for (let i = 0; i <= upToStepIndex; i++) {
      const cat = categories[i];
      if (cat && cat.type === 'global' && state.selections[cat.id]) scoped[cat.id] = state.selections[cat.id];
    }
    return scoped;
  }

  function _updateImage(overrideSelections, overrideLastContrib) {
    if (!elements.image || !Object.keys(imageMap).length) return;
    const sels = overrideSelections !== undefined ? overrideSelections : state.selections;
    const lastContrib = overrideLastContrib !== undefined ? overrideLastContrib : state.lastContributingCategoryId;
    const url = resolveProductImage(sels, imageMap, categories, lastContrib);
    if (!url) return;

    const el = elements.image;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReduced) {
      el.src = url;
      return;
    }

    el.classList.add('planner__image--loading');

    // Preload in parallel; swap src only after fade-out transition ends
    const preloaded = new Image();
    preloaded.src = url;

    let swapped = false;
    const swap = () => {
      if (swapped) return;
      swapped = true;
      el.src = url;
      requestAnimationFrame(() => el.classList.remove('planner__image--loading'));
    };

    el.addEventListener('transitionend', swap, { once: true });
    // Fallback: if transitionend doesn't fire within 400ms (e.g. reduced-motion via CSS, element hidden), force swap
    setTimeout(swap, 400);
  }

  function _buildSummaryHTML() {
    const catItems = categories
      .filter(cat => state.selections[cat.id])
      .map(cat => {
        const optId = state.selections[cat.id];
        const opt = cat.options && cat.options.find(o => o.id === optId);
        return `<div class="planner__summary-item"><dt>${cat.label || cat.id}</dt><dd>${opt ? (opt.label || opt.id) : optId}</dd></div>`;
      })
      .join('');

    let bundleItem = '';
    if (bundleConfig && state.bundleSelection && state.bundleSelection !== 'none') {
      const bundleOpt = bundleConfig[state.bundleSelection];
      if (bundleOpt) {
        bundleItem = `<div class="planner__summary-item"><dt>Bundle</dt><dd>${bundleOpt.label || state.bundleSelection}</dd></div>`;
      }
    }

    return catItems + bundleItem;
  }

  function _updateSummary() {
    if (!elements.summary) return;
    const items = _buildSummaryHTML();
    elements.summary.innerHTML = items ? `<dl class="planner__summary-list">${items}</dl>` : '';
  }

  function _updatePrice() {
    if (!elements.priceEl || !skuPriceMap) return;
    const result = derivePrice(state.selections, categories, skuPrefix, skuPriceMap);
    if (!result) return;

    if (state.bundleSelection !== 'none' && bundleConfig?.[state.bundleSelection] && bundleVariantMap) {
      const colorLabel = _getBundleColorLabel();
      const opt = bundleConfig[state.bundleSelection];
      const variant = lookupBundleVariant(bundleVariantMap, state.bundleSelection, colorLabel);
      if (variant) {
        const plannerCents = (!result.isFromPrice && result.cents) ? result.cents : 0;
        const { original, discounted } = formatBundlePrice(plannerCents + variant.price, opt.discount_rate);
        if (discounted) {
          const s = document.createElement('s');
          s.textContent = original;
          elements.priceEl.textContent = '';
          elements.priceEl.append(s, ` ${discounted} gesamt`);
          return;
        }
      }
    }

    elements.priceEl.textContent = result.isFromPrice ? 'Ab ' + result.formatted : result.formatted;
  }

  function _updateOverviewDialog() {
    if (!elements.overviewBody) return;
    const items = _buildSummaryHTML();
    elements.overviewBody.innerHTML = items
      ? `<dl class="planner__summary-list">${items}</dl>`
      : '<p>Noch keine Auswahl getroffen.</p>';
  }

  function _preloadStepImages(index) {
    const cat = categories[index];
    if (!cat || !cat.contributes_to_image || !Array.isArray(cat.options)) return;
    // Build global key prefix from current global selections
    const globalParts = [];
    for (const c of categories) {
      if (c.type !== 'global') continue;
      const optId = state.selections[c.id];
      if (!optId) return; // can't preload without global keys
      const opt = c.options && c.options.find(o => o && o.id === optId);
      globalParts.push(opt && opt.image_key ? opt.image_key : optId);
    }
    if (globalParts.length === 0) return;
    const globalKey = globalParts.join('__');
    for (const opt of cat.options) {
      if (!opt || !opt.image_key) continue;
      if (cat.image_key_prefix_from) {
        const prefixCat = categories.find(c => c.id === cat.image_key_prefix_from);
        if (prefixCat && Array.isArray(prefixCat.options)) {
          for (const prefixOpt of prefixCat.options) {
            if (!prefixOpt || !prefixOpt.image_key) continue;
            const compoundUrl = imageMap[globalKey + '__' + prefixOpt.image_key + '__' + opt.image_key];
            if (compoundUrl) { const img = new Image(); img.src = compoundUrl; }
          }
        }
      }
      const url = imageMap[globalKey + '__' + opt.image_key];
      if (url) { const img = new Image(); img.src = url; } // fire-and-forget; browser caches
    }
  }

  function _preloadDefaultImages() {
    if (!imageMap || Object.keys(imageMap).length === 0) return;
    const sim = { ...state.selections };
    let lastContrib = state.lastContributingCategoryId;
    for (let i = 1; i < categories.length; i++) {
      const cat = categories[i];
      if (resolveHiddenCategories(categories, sim).has(cat.id)) continue;
      if (sim[cat.id]) continue;
      const defaultOptId = getDefaultPreselection(i, categories, sim);
      if (!defaultOptId) continue;
      sim[cat.id] = defaultOptId;
      if (cat.contributes_to_image) {
        lastContrib = cat.id;
        const url = resolveProductImage(sim, imageMap, categories, lastContrib);
        if (url) { const img = new Image(); img.src = url; }
      }
    }
  }

  function _syncURL() {
    let qs = encodeConfigToURL(state.selections, state.currentStep);
    const bundleParam = encodeBundleSelection(state.bundleSelection);
    if (bundleParam) qs = (qs ? qs + '&' : '') + 'bundle=' + bundleParam;
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }

  async function _rawCartFetch(payload, url) {
    // Same as addToCart() but WITHOUT dispatching cart:updated — the animation
    // layer dispatches it manually after the success animation completes.
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = data.description || data.message || (res.status === 422 ? 'Artikel nicht verfügbar.' : 'Fehler beim Hinzufügen.');
        return { ok: false, error: message };
      }
      return { ok: true, data };
    } catch (_) {
      return { ok: false, error: 'Netzwerkfehler. Bitte erneut versuchen.' };
    }
  }

  async function _onAddToCart() {
    if (!elements.cartBtn) return;
    if (skuMapLoading) {
      if (elements.errorEl) {
        elements.errorEl.textContent = 'Varianten werden noch geladen, bitte einen Moment warten.';
        elements.errorEl.hidden = false;
      }
      return;
    }
    if (!canAddToCart(state.selections, categories)) {
      const hiddenIds = resolveHiddenCategories(categories, state.selections);
      const visibleCats = categories.filter(c => !hiddenIds.has(c.id));
      const missingIdx = visibleCats.findIndex(c => !state.selections[c.id]);
      if (elements.errorEl) {
        elements.errorEl.hidden = false;
        elements.errorEl.textContent = '';
        if (missingIdx >= 0) {
          const missing = visibleCats[missingIdx];
          const msg = document.createTextNode(`Schritt ${missingIdx + 1} (${missing.label || missing.id}) ist noch offen. `);
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.setAttribute('data-planner-error-goto', '');
          btn.setAttribute('style', 'background:none;border:none;padding:0;font:inherit;text-decoration:underline;cursor:pointer');
          btn.textContent = 'Öffnen';
          btn.addEventListener('click', () => _goToStep(missingIdx), { once: true });
          elements.errorEl.append(msg, btn);
        } else {
          elements.errorEl.textContent = 'Bitte alle Schritte ausfüllen.';
        }
      }
      return;
    }

    const orderedIds = _getOrderedIds();
    let payload;
    if (skuMap) {
      const skuResult = buildSkuCartPayload(state.selections, categories, skuPrefix, skuMap);
      if (skuResult.error) {
        if (elements.errorEl) { elements.errorEl.textContent = skuResult.error; elements.errorEl.hidden = false; }
        return;
      }
      payload = skuResult;
    } else {
      payload = buildCartPayload(state.variantId, state.selections, orderedIds, categories);
      if (!payload) return;
    }

    if (state.bundleSelection !== 'none' && bundleConfig?.[state.bundleSelection]) {
      const colorLabel = _getBundleColorLabel();
      const bundleVariant = lookupBundleVariant(bundleVariantMap, state.bundleSelection, colorLabel);
      if (bundleVariant) {
        payload = {
          items: [
            { id: payload.id, quantity: payload.quantity, properties: payload.properties },
            buildBundleCartItem(bundleVariant.id)
          ]
        };
      }
    }

    const cartBase = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add';
    const url = cartBase.endsWith('.js') ? cartBase : cartBase + '.js';

    // Include sections so Shopify returns rendered cart drawer HTML
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      payload = { ...payload, sections: cartDrawer.getSectionsToRender().map(s => s.id) };
    }

    // Fade out configurator content, show animation overlay
    if (elements.stepsCol) {
      elements.stepsCol.style.opacity = '0';
      elements.stepsCol.style.pointerEvents = 'none';
    }
    await new Promise(r => setTimeout(r, 300));

    if (elements.animOverlay) elements.animOverlay.hidden = false;
    if (elements.stepsCol) {
      elements.stepsCol.style.opacity = '';
      elements.stepsCol.style.pointerEvents = '';
    }

    // Fetch + minimum display time in parallel
    const [result] = await Promise.all([
      _rawCartFetch(payload, url),
      new Promise(r => setTimeout(r, minAnimDuration))
    ]);

    if (!result.ok) {
      // Hide overlay, show error, re-enable cart button
      if (elements.animOverlay) elements.animOverlay.hidden = true;
      if (elements.errorEl) { elements.errorEl.textContent = result.error; elements.errorEl.hidden = false; }
      _render();
      return;
    }

    // Show success animation
    if (elements.animOverlay) elements.animOverlay.classList.add('is-success');

    // Wait for checkmark animation, then open cart drawer (or fall back to event)
    await new Promise(r => setTimeout(r, 700));
    if (cartDrawer && result.data && result.data.sections) {
      cartDrawer.renderContents(result.data);
    } else {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: result.data || {} }));
    }

    // Clear stored and in-memory state — next visit starts fresh (#1)
    clearState(state.productId);
    state.selections = {};
    state.currentStep = 0;
    state.completed = false;
    state.lastContributingCategoryId = null;
    state.dependencyInvalidatedIds = new Set();
    state.bundleSelection = 'none';
    if (elements.animOverlay) {
      elements.animOverlay.classList.remove('is-success');
      elements.animOverlay.hidden = true;
    }
    elements.steps.forEach((step, i) => { step.hidden = i !== 0; });
    if (elements.bundleStep) elements.bundleStep.hidden = true;
    _render();
    _updateImage();
    _updateSummary();
    _updatePrice();
    _syncURL();
  }

  return { init };
})();

/* c8 ignore end */
if (typeof document !== 'undefined') {
  document.addEventListener('DOMContentLoaded', () => PlannerConfigurator.init());
}
